import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input, OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';

// models
import { CustomPaginatorIntl } from '../../models/customPaginatorIntl';
import {
    DType,
    IColumnType,
    IRowType,
    ITableData,
    ITableExcelResponse,
    ITableExcelRow,
    ITableResults
} from '../../models/table';
import { ICurrentTableState } from '../../models/currentTableState';
import { ITableSettingColumnsData } from '../../models/tableSettingColumn';
import { IUserTableSettings } from '../../models/userTableSettings';
import { TableSettings } from '../../models/tableSettings';
import { ISearchSettings } from '../../../product/models/models';

// components
import { ColorTableRowPipe } from '../../pipes/color-table-row.pipe';
import { ColumnsSettingsModalComponent } from '../../modals/columns-settings-modal/columns-settings-modal.component';

// services
import { SessionService } from '../../services/session.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { TableService } from '../../services/table.service';

import { SHARED_MODULES } from '../../../shared.';
import { DtypeInjectorPipe } from '../../pipes/dtype-injector.pipe';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { LoaderComponent } from '../loader/loader.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { SearchComponent } from '../search/search.component';

@Component({
    selector: 'app-ph-table[data]',
    templateUrl: './ph-table.component.html',
    styleUrls: ['./ph-table.component.scss'],
	imports: [
		SHARED_MODULES,
		ColorTableRowPipe,
		DtypeInjectorPipe,
		FaIconComponent,
		LoaderComponent,
		PhIconComponent,
		PhIconWrapperComponent,
		SearchComponent
	],
    providers: [
        { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl },
        ColorTableRowPipe
    ],
    standalone: true
})
export class PhTableComponent implements OnInit, AfterViewInit, OnDestroy {

    columnsData: ITableSettingColumnsData; // columns that are required for the columns settings modal
    columns: Array<IColumnType> = [];
    columnWidths: { [key: string]: number } = {}; // object with the column sizes so that they can be saved after updating the data
    currentState: Partial<ICurrentTableState>;
    data: ITableData;
    dataSource: MatTableDataSource<IRowType>;
    displayedColumns: Array<string>; // an array with column keys
    dtypes: DType;
    isDragging: boolean = false;
    openGroupSubscriptions: Array<Subscription> = [];
    showLoader: boolean = false;
    showSaveLoader: boolean = false;
    userSettings: IUserTableSettings;

    @Input() loadDataCallback: (key?: string | number) => Observable<ITableResults>; // use for load children and full data for xls
    @Input() loadColumnsCallback: () => Observable<ITableSettingColumnsData>;
	@Input() customClass: string;
    @Input() name: string = '';
    @Input() settings: TableSettings = new TableSettings();
    @Input() initialSearchQuery: string = '';
    @Input() savedParams: {[key: string]: any};
    @Input() settingsKey: string;
    @Input() filtersApplied: boolean = false;
    @Input('currentState') set setCurrentState(state: Partial<ICurrentTableState>) {
        if (state?.sortingField) {
            state.sortingField = this.columns
                .find((column: IColumnType) => column.key === state.sortingField)
                ?.title;
        }
        this.currentState = {...state};
    };
    @Input('data') set setData(data: ITableData) {
        this.initDataSource(data);
    }
	@Input() searchSettings: Array<ISearchSettings> = [];

    @Output('columnsSortUpdate') onColumnsSortUpdateEmitter: EventEmitter<Array<string>> = new EventEmitter<Array<string>>();
    @Output('columnsUpdate') onColumnsUpdateEmitter: EventEmitter<Array<string>> = new EventEmitter<Array<string>>();
    @Output('filtersOpen') onFiltersOpenEmitter: EventEmitter<void> = new EventEmitter<void>();
    @Output('pageUpdate') onPageUpdateEmitter: EventEmitter<{ page: number, pageSize: number }> =
        new EventEmitter<{ page: number, pageSize: number }>();
    @Output('search') onSearchEmitter: EventEmitter<string> = new EventEmitter<string>();
    @Output('sorting') onSortingEmitter: EventEmitter<string> = new EventEmitter<string>();
	@Output('downloadExcel') downloadExcelEmitter: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild('tableContainer') tableContainer: ElementRef<HTMLDivElement>;

	@ViewChildren('columnRef') columnsRefs: QueryList<ElementRef>;

    constructor(
        private dialog: MatDialog,
        private colorTableRowPipe: ColorTableRowPipe,
        private sessionService: SessionService,
        private snackBarService: SnackBarService,
        private tableService: TableService
    ) {}

	get isSearchSettingsActive(): boolean {
		return this.searchSettings?.some(f => f.selected);
	}

    ngOnInit(): void {
        this.dtypes = this.tableService.dtype;
        if (this.sessionService.defaults.settings[this.settingsKey]) {
            this.userSettings = this.sessionService.defaults.settings[this.settingsKey];
        }
        this.dataSource.paginator = this.paginator;

        if (this.loadColumnsCallback) {
            this.loadColumnsCallback().subscribe({
                next: (response: ITableSettingColumnsData) => this.columnsData = response,
                error: () => this.snackBarService.setErrorNotification()
            });
        }
    }

    ngAfterViewInit(): void {
		if (this.paginator) this.paginator.page.subscribe((event: PageEvent) => this.onPageUpdate(event));

        this.sort.sortChange
            .pipe(debounceTime(1000))
            .subscribe((event: Sort) => this.onSortingChange(event));
    }

    autoResizeColumn(columnElement: HTMLTableCellElement, event: MouseEvent): void {
        if ((event.target as HTMLElement).localName === 'th') {
            columnElement.style.width = 'auto'
        }
    }

    closeGroup(element: IRowType, needClose: boolean = true): void {
        if (Array.isArray(element.children)) {
            for (let i: number = 0; i < element.children.length; i++) {
                this.closeGroup(element.children[i], false);
            }
        }
        if (!element.opened) return;
        element.opened = !needClose;
        const index: number = this.dataSource.data.indexOf(element),
            data: Array<IRowType> = this.dataSource.data;
        if (index === -1) return;

        data.splice(index + 1, element.children.length);
        this.dataSource = new MatTableDataSource<IRowType>(data);
    }

    dragListDropped(event: CdkDragDrop<Array<string>>): void {
        if (!event) return;
        if (event.currentIndex === event.previousIndex) return;
        this.setColumnsWidths();

        const stickyColumnsLength: number = this.settings.stickyFirstColumn ? 1 : 0,
            previousIndex: number = event.previousIndex + stickyColumnsLength,
            currentIndex: number = event.currentIndex + stickyColumnsLength,
            metricsPreviousIndex: number = this.data.metrics.indexOf(this.displayedColumns[previousIndex]),
            metricsCurrentIndex: number = this.data.metrics.indexOf(this.displayedColumns[currentIndex]);

        moveItemInArray(
            this.data.metrics,
            metricsPreviousIndex,
            metricsCurrentIndex
        );
        moveItemInArray(
            this.displayedColumns,
            previousIndex,
            currentIndex
        );
        this.onColumnsSortUpdateEmitter.emit(this.data.metrics);
    }

    exportXLS(): Observable<ITableExcelResponse> {
        if (!this.loadDataCallback) return;

        return this.loadDataCallback().pipe(
            map((response: ITableResults) => {
                const data: Array<IRowType> = response.dataSource,
                    tableData: Array<IRowType> = this.dataSource.data;

                for (let i: number = 0; i < tableData.length; i++) {
                    const row: IRowType = tableData[i];

                    if (row.children && Array.isArray(row.children)) {
                        const foundRow: IRowType = data.find((element: IRowType) => element.key === row.key);

                        if (foundRow) foundRow.children = row.children;
                    }
                }

                const excelData: Array<ITableExcelRow> = [];

                const addRowToExcel = (row: IRowType) => {
                    const {
                        children,
                        key,
                        opened,
                        level,
                        clickable_params,
                        ...results
                    } = row;

                    excelData.push({
                        results: results,
                        level: (level || 0) + 1,
                        level_color: this.colorTableRowPipe
                            .transform(row, this.data.max_levels)
                    });

                    if (row.children && Array.isArray(row.children)) {
                        for (let i: number = 0; i < row.children.length; i++) {
                            addRowToExcel(row.children[i]);
                        }
                    }
                }

                for (let i: number = 0; i < data.length; i++) {
                    addRowToExcel(data[i]);
                }

                return {
                    data: excelData,
                    column_titles: response.columns.map((column: IColumnType) => column.title)
                };
            })
        );
    }

    initDataSource(data: ITableData): void {
        this.data = data;
        this.dataSource = new MatTableDataSource<IRowType>(this.data.results.dataSource);
        this.setDisplayedColumns();
        this.showLoader = false;
    }

    onPageUpdate(event: PageEvent): void {
        this.onPageUpdateEmitter.emit({
            page: event.pageIndex + 1,
            pageSize: event.pageSize
        });
    }

	onSearch(value: string): void {
		this.onSearchEmitter.emit(value.trim().toLowerCase());
    }

    onSortingChange(event: Sort): void {
        let value: string = !event.direction ? '' : event.active;
        if (event.direction === 'desc') value = '-' + value;
        this.onSortingEmitter.emit(value);
    }

	onDownloadFile(): void {
		this.downloadExcelEmitter.emit();
	}

    onViewSave(): void {
        if (!this.settingsKey) return;
        this.showSaveLoader = true;
        const prevSettings: IUserTableSettings = this.userSettings ? JSON.parse(JSON.stringify(this.userSettings)) : null;

        const updateParams: IUserTableSettings = {
            columns: this.data.metrics
        };
		if (this.paginator) updateParams.pageSize = this.paginator.pageSize;
        if (this.savedParams) Object.assign(updateParams, this.savedParams);

        (this.sessionService.defaults.settings[this.settingsKey] as IUserTableSettings) = updateParams;
        this.sessionService.updateSettings$().subscribe({
            next: () => {
                this.snackBarService.setSnackBarNotification({
                    messageText: 'COMMON.SAVED_TABLE_VIEW',
                    closeActions: [
                        {
                            action: 'COMMON.CANCEL',
                            callback: () => {
                                (this.sessionService.defaults.settings[this.settingsKey] as IUserTableSettings) = prevSettings;
                                this.sessionService.updateSettings$().subscribe();
                            }
                        }
                    ]
                });
                this.showSaveLoader = false;
            },
            error: () => {
                this.snackBarService.setErrorNotification();
                this.showSaveLoader = false;
            }
        });
    }

    openColumnsModal(): void {
        const dialogRef: MatDialogRef<ColumnsSettingsModalComponent> = this.dialog.open(ColumnsSettingsModalComponent, {
            width: '700px',
            autoFocus: false,
            backdropClass: 'back-drop',
            panelClass: 'scrollable-content',
            data: {
                allColumns: this.columnsData,
                visibleColumns: this.data.metrics
            }
        });

        dialogRef.afterClosed().subscribe((result: Array<string>) => {
            if (!result) return;
            const newColumns: Array<string> = result.filter((column: string) => !this.data.metrics.includes(column)),
                oldColumns: Array<string> = this.data.metrics.filter((column: string, index: number) => {
                    return result.includes(column) || (!index && this.settings.stickyFirstColumn);
                });

            let columns: Array<string> = [];
            if (this.settings.draggableColumns) {
                columns = oldColumns.concat(newColumns);
            } else {
                columns = result;
                if (this.settings.stickyFirstColumn) columns.unshift(this.data.metrics[0]);
            }

            this.onColumnsUpdateEmitter.emit(columns);
        });
    }

    openGroup(element: IRowType): void {
        if (!this.loadDataCallback) return;
        element.opened = true;

        if (!Object.keys(element.children).length) {
            this.openGroupSubscriptions.push(
                this.loadDataCallback(element.key).subscribe({
                    next: (response: ITableResults) => {
                        const index: number = this.dataSource.data.indexOf(element),
                            data: Array<IRowType> = this.dataSource.data;
                        if (index === -1) return;

                        element.children = response.dataSource;
                        element.children.forEach((child: IRowType) => child.level = (element.level || 0) + 1);
                        data.splice(index + 1, 0, ...element.children);
                        this.dataSource = new MatTableDataSource<IRowType>(data);
                    },
                    error: () => {
                        this.snackBarService.setErrorNotification();
                        element.opened = false;
                    }
                })
            );
        } else {
            const index: number = this.dataSource.data.indexOf(element),
                data: Array<IRowType> = this.dataSource.data;
            if (index === -1) return;

            data.splice(index + 1, 0, ...element.children);
            this.dataSource = new MatTableDataSource<IRowType>(data);

            for (let i: number = 0; i < element.children.length; i++) {
                const child: IRowType = element.children[i];
                if (child.opened) this.openGroup(child);
            }
        }
    }

    openFilters(): void {
        this.onFiltersOpenEmitter.emit();
    }

    setColumnsWidths(): void {
        if (!this.columnsRefs) return;
        this.columnsRefs.forEach((column: ElementRef) => {
            this.columnWidths[column.nativeElement.id] = column.nativeElement.getBoundingClientRect().width;
        });
    }

    setDisplayedColumns(): void {
        this.setColumnsWidths();
        this.columns = this.data.results.columns;
        this.displayedColumns = this.columns.map((column: IColumnType) => column.key);
    }

    startLoading(needScroll?: boolean, needPageReset?: boolean): void {
        this.openGroupSubscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
        this.showLoader = true;
        if (needScroll && this.tableContainer) {
            this.tableContainer.nativeElement.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
        if (needPageReset && this.paginator) {
            this.paginator.pageIndex = 0;
        }
    }

    trackByFn(_: number, element: IRowType): number | string {
        return element.key;
    }

    trackByColumns(_: number, element: IColumnType): number | string {
        return element.key;
    }

    ngOnDestroy(): void {
        this.openGroupSubscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    }
}
